/**
 * ProfileConnector component for managing app profile connections
 * Handles profile selection, creation, and authentication workflow
 */

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from "@/components/ui";
import { api, cn } from "@/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronRight,
  ExternalLink,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Plus,
  Save,
  Search,
  Settings,
  User,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AuthConfigField,
  ComposioProfile,
  ComposioTool,
  IntegrationApp,
  Step,
  StepConfig,
  ToolkitDetails,
  ToolsResponse,
} from "./types";

/**
 * Props for the ProfileConnector component
 * Manages profile connection workflow for app integrations
 */
interface ProfileConnectorProps {
  /**
   * Integration app configuration to connect
   * Contains app metadata and connection details
   */
  app: IntegrationApp;

  /**
   * Dialog visibility state
   * Controls whether the profile connector dialog is shown
   */
  open: boolean;

  /**
   * Callback to handle dialog visibility changes
   * Called when dialog should be opened or closed
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Callback for successful profile connection
   * Called when profile is connected and ready for use
   */
  onProfileConnected: (profile: ComposioProfile) => void;

  /**
   * Whether to show tools preview panel
   * Optional feature for displaying available tools
   */
  showToolsPreview?: boolean;

  /**
   * Whether to show the back button in profile creation step
   * Controls navigation flow based on parent component needs
   */
  showBackButton?: boolean;

  /**
   * Callback when back button is clicked
   * Called when user wants to go back in the workflow
   */
  onBack?: () => void;

  /**
   * Callback for step changes
   * Emits current step and its configuration for parent title management
   */
  onStepChange?: (step: Step, config: StepConfig) => void;

  /**
   * Callback for title changes
   * Emits current title and description for parent dialog
   */
  onTitleChange?: (title: string, description: string) => void;

  /**
   * Whether the component is embedded in a parent dialog
   * Controls styling and behavior for nested usage
   */
  isEmbedded?: boolean;

  /**
   * Whether to show the header section
   * Optional for parent components that manage their own header
   */
  showHeader?: boolean;
}


/**
 * Props for initiation field input component
 * Handles authentication field inputs
 */
interface InitiationFieldInputProps {
  /**
   * Field configuration and validation rules
   * Defines input type and requirements
   */
  field: AuthConfigField;

  /**
   * Current field value
   * The user-entered data for this field
   */
  value: string;

  /**
   * Callback for value changes
   * Called when user modifies the input
   */
  onChange: (value: string) => void;

  /**
   * Optional error message to display
   * Shown when validation fails
   */
  error?: string;
}

/**
 * Props for tool preview card component
 * Displays tool information in preview mode
 */
interface ToolPreviewCardProps {
  /**
   * Tool configuration and metadata
   * Contains tool details for display
   */
  tool: ComposioTool;

  /**
   * Current search/filter term
   * Used for highlighting matching text
   */
  searchTerm: string;
}

/**
 * Configuration for profile connection workflow steps
 * Defines step metadata including titles, descriptions, and icons
 */
const profileStepConfigs: StepConfig[] = [
  {
    id: Step.ProfileSelect,
    title: "Connect & Preview",
    description: "Choose profile and explore available tools",
    icon: <User className="w-4 h-4" />,
    showInProgress: true,
  },
  {
    id: Step.ProfileCreate,
    title: "Create Profile",
    description: "Create a new profile to connect your tools",
    icon: <Plus className="h-4 w-4" />,
    showInProgress: true,
  },
  {
    id: Step.Connecting,
    title: "Authenticate",
    description: "Authenticate your profile to connect your tools",
    icon: <ExternalLink className="h-4 w-4" />,
    showInProgress: true,
  },
  {
    id: Step.Success,
    title: "Complete",
    description: "Successfully connected",
    icon: <Check className="h-4 w-4" />,
    showInProgress: false,
  },
];

/**
 * Gets the index position of a step in the workflow
 * Used for step progression and validation logic
 */
const getStepIndex = (step: Step): number => {
  return profileStepConfigs.findIndex((config) => config.id === step);
};

/*
const StepIndicator = ({ currentStep }) => {
  const currentIndex = getStepIndex(currentStep);
  const visibleSteps = profileStepConfigs;

  const visibleCurrentIndex = visibleSteps.findIndex(
    (step) => step.id === currentStep
  );

  return (
    <div className="pt-4 pb-3">
      <div className="flex items-center justify-between relative">
        <div className="absolute left-0 right-0 top-[14px] h-[2px] bg-muted-foreground/20 -z-10" />
        <motion.div
          className="absolute left-0 top-[14px] h-[2px] bg-primary -z-10"
          initial={{ width: 0 }}
          animate={{
            width: `${
              (visibleCurrentIndex / (visibleSteps.length - 1)) * 100
            }%`,
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />

        {visibleSteps.map((step, index) => {
          const stepIndex = getStepIndex(step.id);
          const isCompleted = stepIndex < currentIndex;
          const isCurrent = step.id === currentStep;
          const isUpcoming = stepIndex > currentIndex;

          return (
            <motion.div
              key={step.id}
              className="flex flex-col items-center gap-2 relative"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="bg-background p-1 rounded-full">
                <div
                  className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 relative",
                    isCompleted && "bg-primary text-primary-foreground",
                    isCurrent &&
                      "bg-primary text-primary-foreground shadow-lg shadow-primary/25",
                    isUpcoming &&
                      "bg-muted-foreground/20 text-muted-foreground",
                    isCurrent && "ring-4 ring-primary/20"
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <div className="h-3 w-3 flex items-center justify-center">
                      {step.icon}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
*/

/**
 * Input component for authentication field configuration
 * Handles different field types with proper validation
 */
const InitiationFieldInput = ({
  field,
  value,
  onChange,
  error,
}: InitiationFieldInputProps) => {
  /**
   * Determines appropriate HTML input type based on field configuration
   * Maps field types to proper HTML input types for better UX
   */
  const getInputType = (fieldType: string) => {
    switch (fieldType.toLowerCase()) {
      case "password":
        return "password";
      case "email":
        return "email";
      case "url":
        return "url";
      case "number":
      case "double":
        return "number";
      case "boolean":
        return "checkbox";
      default:
        return "text";
    }
  };

  const inputType = getInputType(field.type);
  const isBooleanField = field.type.toLowerCase() === "boolean";
  const isNumberField =
    field.type.toLowerCase() === "number" ||
    field.type.toLowerCase() === "double";

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={field.name} className="text-sm font-medium">
          {field.displayName}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </Label>
      </div>

      {isBooleanField ? (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id={field.name}
            checked={value === "true"}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            className="h-4 w-4"
          />
          <Label htmlFor={field.name} className="text-sm text-muted-foreground">
            {field.description || `Enable ${field.displayName.toLowerCase()}`}
          </Label>
        </div>
      ) : (
        <Input
          id={field.name}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            field.default || `Enter ${field.displayName.toLowerCase()}`
          }
          className={cn(error && "border-destructive focus:border-destructive")}
          step={isNumberField ? "any" : undefined}
        />
      )}

      {field.description && !isBooleanField && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{field.description}</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 text-xs text-destructive">
          <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Preview card component for displaying tool information
 * Shows tool details with search term highlighting
 */
const ToolPreviewCard = ({ tool, searchTerm }: ToolPreviewCardProps) => {
  /**
   * Highlights search terms within text content
   * Creates marked elements for matching text portions
   */
  const highlightText = (text: string, term: string) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-900 px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  /**
   * Generates appropriate icon based on tool functionality
   * Uses tool name patterns to determine suitable icon
   */
  const getToolIcon = (toolName: string) => {
    const name = toolName.toLowerCase();
    if (
      name.includes("create") ||
      name.includes("add") ||
      name.includes("new")
    ) {
      return <Plus className="w-3 h-3" />;
    }
    if (
      name.includes("get") ||
      name.includes("fetch") ||
      name.includes("read") ||
      name.includes("view")
    ) {
      return <Eye className="w-3 h-3" />;
    }
    if (
      name.includes("update") ||
      name.includes("edit") ||
      name.includes("modify")
    ) {
      return <Settings className="w-3 h-3" />;
    }
    if (
      name.includes("send") ||
      name.includes("post") ||
      name.includes("message")
    ) {
      return <ChevronRight className="w-3 h-3" />;
    }
    if (name.includes("search") || name.includes("find")) {
      return <Search className="w-3 h-3" />;
    }
    if (
      name.includes("user") ||
      name.includes("profile") ||
      name.includes("account")
    ) {
      return <User className="w-3 h-3" />;
    }
    return (
      <div className="w-3 h-3 rounded-sm bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
        {toolName.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <div
      className="border rounded-md p-2 hover:bg-muted/30 transition-colors group cursor-pointer"
      title={tool.description}
    >
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">{getToolIcon(tool.name)}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">
            {highlightText(tool.name, searchTerm)}
          </div>
          {tool.tags && tool.tags.length > 0 && (
            <div className="text-sm text-muted-foreground truncate">
              {tool.tags[0]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Main profile connector component for managing profile connections
 * Handles the complete workflow from profile selection to authentication
 */
export const ProfileConnector: React.FC<ProfileConnectorProps> = ({
  app,
  open,
  onOpenChange,
  onProfileConnected,
  showToolsPreview = false,
  showBackButton = true,
  onBack,
  onStepChange,
  onTitleChange,
  isEmbedded = false,
}) => {
  /**
   * Current step in the connection workflow
   * Controls which UI step is displayed to the user
   */
  const [currentStep, setCurrentStep] = useState<Step>(Step.ProfileSelect);

  /**
   * Profile name for new connection
   * Default name generated from app name
   */
  const [profileName, setProfileName] = useState(`${app.name} Profile`);

  /**
   * ID of currently selected profile
   * Used when connecting to existing profiles
   */
  const [selectedProfileId, setSelectedProfileId] = useState<string>("");

  /**
   * ID of profile created during workflow
   * Stored after successful profile creation
   */
  const [createdProfileId, setCreatedProfileId] = useState<string | null>(null);
  const [createdConnectedAccountId, setCreatedConnectedAccountId] = useState<string | null>(null);

  /**
   * Loading state for tools preview
   * Shows loading indicator while fetching tools
   */
  const [isLoadingToolsPreview, setIsLoadingToolsPreview] = useState(false);

  /**
   * Search term for filtering tools preview
   * Used to filter displayed tools by name or description
   */
  const [toolsPreviewSearchTerm] = useState("");

  /**
   * Visibility state for tools preview panel
   * Controls whether tools preview is expanded
   */
  const [showToolsManager, setShowToolsManager] = useState(false);

  /**
   * Animation direction for step transitions
   * Determines slide animation direction between steps
   */
  const [direction, setDirection] = useState<"forward" | "backward">("forward");

  /**
   * Type of connection being made
   * Determines whether to use existing or create new profile
   */
  const [selectedConnectionType, setSelectedConnectionType] = useState<
    "existing" | "new" | null
  >(null);

  /**
   * Authentication field values
   * Stores user input for connection parameters
   */
  const [initiationFields, setInitiationFields] = useState<
    Record<string, string>
  >({});

  /**
   * Validation errors for authentication fields
   * Contains error messages for invalid field values
   */
  const [initiationFieldsErrors, setInitiationFieldsErrors] = useState<
    Record<string, string>
  >({});

  /**
   * Loading state for profile fetching
   * Shows loading indicator while fetching user profiles
   */
  const [, setIsLoadingProfiles] = useState(true);

  /**
   * Available user profiles for this app
   * List of existing connections that can be reused
   */
  const [profiles, setProfiles] = useState<ComposioProfile[]>([]);

  /**
   * Available tools for the integration
   * List of tools that can be selected and configured
   */
  const [toolsResponse, setToolsResponse] = useState<ToolsResponse>([]);

  /**
   * Loading state for toolkit details
   * Shows loading indicator while fetching connection requirements
   */
  const [isLoadingToolkitDetails, setIsLoadingToolkitDetails] = useState(false);

  /**
   * Toolkit connection requirements
   * Contains authentication fields and configuration options
   */
  const [toolkitDetails, setToolkitDetails] = useState<ToolkitDetails | null>(
    null
  );

  /**
   * Auth config details extracted from toolkit details.
   * Contains available auth modes (API_KEY, OAUTH2, etc.) and their required fields.
   */
  const authConfigDetails = useMemo(
    () => toolkitDetails?.toolkit.authConfigDetails || [],
    [toolkitDetails]
  );

  /**
   * Available auth modes for this toolkit derived from authConfigDetails.
   * Used to show auth method selector when multiple modes are available.
   */
  const availableAuthModes = useMemo(() =>
    authConfigDetails.map((config: { mode: string }) => config.mode),
    [authConfigDetails]
  );

  /** Whether this toolkit has authConfigDetails with at least one mode */
  const hasAuthConfig = authConfigDetails.length > 0;

  /** Whether this toolkit supports multiple auth methods */
  const hasMultipleAuthModes = availableAuthModes.length > 1;

  /**
   * Selected authentication method for the current toolkit connection.
   * Defaults to API_KEY if available, otherwise falls back to first available mode.
   */
  const [selectedAuthMethod, setSelectedAuthMethod] = useState<"API_KEY" | "OAUTH2">("API_KEY");

  /** Sync default auth method when toolkit details load */
  useEffect(() => {
    if (availableAuthModes.length > 0) {
      const defaultMode = availableAuthModes.includes("API_KEY") ? "API_KEY" : availableAuthModes[0];
      setSelectedAuthMethod(defaultMode as "API_KEY" | "OAUTH2");
    }
  }, [availableAuthModes]);

  /**
   * Custom OAuth credentials state for WhatsApp
   */
  const [useCustomCredentials, setUseCustomCredentials] = useState(false);
  const [customOAuthFields, setCustomOAuthFields] = useState<Record<string, string>>({});

  /**
   * Loading state for profile creation
   * Shows loading indicator during profile creation process
   */
  const [_, setIsCreating] = useState(false);

  /**
   * OAuth redirect URL for authentication
   * Opens in popup window for OAuth flow
   */
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  /**
   * Connection state for profile creation
   * Shows loading indicator during connection process
   */
  const [isConnecting, setIsConnecting] = useState(false);

  /**
   * Fetches available user profiles from the API
   * Loads profiles that can be used for app connections
   */
  const fetchProfiles = useCallback(async () => {
    setIsLoadingProfiles(true);
    try {
      const response = await api.get("/composio/profiles");
      setProfiles(response.data?.profiles || []);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    } finally {
      setIsLoadingProfiles(false);
    }
  }, []);

  /**
   * Fetches available tools for a specific toolkit
   * Retrieves tools that can be selected for integration
   */
  async function fetchToolsList(toolkit_slug: string, limit: number) {
    setIsLoadingToolsPreview(true);
    try {
      const response = await api.post(`/composio/tools/list`, {
        slug: toolkit_slug,
        limit,
      });

      setToolsResponse(response.data || []);
    } catch (error) {
      console.error("Failed to fetch tools list:", error);
      setToolsResponse([]);
    } finally {
      setIsLoadingToolsPreview(false);
    }
  }

  /**
   * Filtered tools based on search term
   * Memoized list of tools matching the current search criteria
   */
  const filteredToolsPreview = useMemo(() => {
    return (toolsResponse || []).filter(
      (tool: ComposioTool) =>
        !toolsPreviewSearchTerm ||
        tool.name
          .toLowerCase()
          .includes(toolsPreviewSearchTerm.toLowerCase()) ||
        tool.description
          .toLowerCase()
          .includes(toolsPreviewSearchTerm.toLowerCase()) ||
        tool.tags?.some((tag: string) =>
          tag.toLowerCase().includes(toolsPreviewSearchTerm.toLowerCase())
        )
    );
  }, [toolsResponse, toolsPreviewSearchTerm]);

  useEffect(() => {
    if (open && currentStep === Step.ProfileSelect) {
      fetchToolsList(app.slug, 50);
    }
  }, [open, currentStep, app.slug]);

  /**
   * Fetches detailed toolkit configuration information
   * Retrieves authentication requirements and connection settings
   * Uses different endpoints for internal apps vs Composio apps
   */
  const fetchToolkitDetails = useCallback(() => {
    if (!app.slug) return;
    setIsLoadingToolkitDetails(true);

    // Use different endpoints based on app type
    const isInternalApp = (app as IntegrationApp).type === "internal";
    const endpoint = isInternalApp
      ? `/integrations/${app.slug}/connection-config`
      : `/composio/toolkits/${app.slug}/details`;

    api
      .get(endpoint)
      .then((response) => {
        if (isInternalApp) {
          // Internal app response includes toolkit in a compatible format
          setToolkitDetails(response.data);
        } else {
          setToolkitDetails(response.data);
        }
        setIsLoadingToolkitDetails(false);
      })
      .catch((err) => {
        console.error("Failed to fetch toolkit details:", err);
        setIsLoadingToolkitDetails(false);
      });
  }, [app]);

  useEffect(() => {
    if (open && currentStep === Step.ProfileCreate) {
      fetchToolkitDetails();
    }
  }, [open, currentStep, fetchToolkitDetails]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  /**
   * Effect to emit step and title changes to parent component
   * Allows parent to manage dialog title and track progress
   */
  useEffect(() => {
    const stepConfig = profileStepConfigs.find(
      (config) => config.id === currentStep
    );
    if (stepConfig) {
      onStepChange?.(currentStep, stepConfig);
      onTitleChange?.(stepConfig.title, stepConfig.description || "");
    }
  }, [currentStep, onStepChange, onTitleChange]);

  /**
   * Connected profiles for the current app
   * Memoized list of profiles that are already connected and available
   */
  const existingProfiles = useMemo(() => {
    return (
      profiles?.filter((p) => p.toolkitSlug === app.slug && p.isConnected) || []
    );
  }, [profiles, app.slug]);

  /**
   * Validates all authentication field inputs.
   * Uses authConfigDetails for apps that have it, otherwise falls back
   * to connectedAccountInitiationFields for standard apps.
   */
  const validateInitiationFields = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    /** Validate a list of required fields against current initiationFields */
    const validateFields = (fields: { name: string; displayName: string; type: string; required?: boolean }[]) => {
      for (const field of fields) {
        if (field.required) {
          const value = initiationFields[field.name];
          const isEmpty = !value || value.trim() === "";

          if (field.type.toLowerCase() === "boolean") continue;

          if (
            (field.type.toLowerCase() === "number" || field.type.toLowerCase() === "double") &&
            value
          ) {
            if (isNaN(Number(value))) {
              newErrors[field.name] = `${field.displayName} must be a valid number`;
              continue;
            }
          }

          if (isEmpty) {
            newErrors[field.name] = `${field.displayName} is required`;
          }
        }
      }
    };

    if (hasAuthConfig) {
      // Use authConfigDetails-based validation for the selected auth method
      const activeConfig = authConfigDetails.find(
        (config: { mode: string }) => config.mode === selectedAuthMethod
      );
      if (activeConfig) {
        const requiredFields = activeConfig.fields?.connectedAccountInitiation?.required || [];
        validateFields(requiredFields);
      }
    } else {
      // Standard validation for apps without authConfigDetails
      const initiationRequirements = toolkitDetails?.toolkit.connectedAccountInitiationFields;
      if (initiationRequirements?.required) {
        validateFields(initiationRequirements.required);
      }
    }

    setInitiationFieldsErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [initiationFields, toolkitDetails, hasAuthConfig, authConfigDetails, selectedAuthMethod]);

  /**
   * Whether connection can be initiated
   * Checks if all required fields are filled and profile name exists
   */
  const canConnect = useMemo(() => {
    if (!profileName.trim()) return false;
    return validateInitiationFields();
  }, [profileName, validateInitiationFields]);

  /**
   * Effect to reset component state when dialog opens
   * Initializes all state variables to default values
   */
  useEffect(() => {
    if (open) {
      setCurrentStep(Step.ProfileSelect);
      setProfileName(`${app.name} Profile`);
      setSelectedProfileId("");
      setCreatedProfileId(null);
      setCreatedConnectedAccountId(null);
      setRedirectUrl(null);
      setShowToolsManager(false);
      setDirection("forward");
      setSelectedConnectionType(null);
      setInitiationFields({});
      setInitiationFieldsErrors({});
      setIsConnecting(false);
    }
  }, [open, app.name]);

  /**
   * Handles changes to authentication field values
   * Updates field values and clears validation errors
   */
  const handleInitiationFieldChange = (fieldName: string, value: string) => {
    setInitiationFields((prev) => ({ ...prev, [fieldName]: value }));
    if (initiationFieldsErrors[fieldName]) {
      setInitiationFieldsErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  /**
   * Handles changes to custom OAuth credential fields
   * Updates custom OAuth fields for WhatsApp authentication
   */
  const handleCustomOAuthFieldChange = (fieldName: string, value: string) => {
    setCustomOAuthFields((prev) => ({ ...prev, [fieldName]: value }));
  };

  /**
   * Navigates to a specific step with proper animation direction
   * Updates current step and animation direction based on movement
   */
  const navigateToStep = (newStep: Step) => {
    const currentIndex = getStepIndex(currentStep);
    const newIndex = getStepIndex(newStep);
    setDirection(newIndex > currentIndex ? "forward" : "backward");
    setCurrentStep(newStep);
  };

  /**
   * Handles profile selection from existing profiles
   * Either creates new profile or selects existing one
   */
  const handleProfileSelect = () => {
    if (selectedProfileId === "new") {
      navigateToStep(Step.ProfileCreate);
    } else if (selectedProfileId) {
      const profile = existingProfiles.find(
        (p) => p.profileId === selectedProfileId
      );
      if (profile) {
        onProfileConnected(profile);
        // Only close dialog if not embedded (embedded mode lets parent handle navigation)
        if (!isEmbedded) {
          onOpenChange(false);
        }
      }
    }
  };

  /**
   * Creates a new profile with authentication credentials
   * Handles profile creation API call and OAuth redirection
   * Supports both internal apps and Composio apps
   */
  const handleCreateProfile = async () => {
    if (!profileName.trim()) {
      toast.error("Profile name is required");
      return;
    }

    if (!validateInitiationFields()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsCreating(true);

    const isInternalApp = (app as IntegrationApp).type === "internal";

    try {
      if (isInternalApp) {
        // Handle internal app connection (Odoo, GitHub, Gmail, etc.)
        // Get the first connection ID from toolkit details (internal apps typically have one connection type)
        const connectionId = toolkitDetails?.connections?.[0]?.id;

        const payload = {
          name: profileName.trim(),
          credentials: initiationFields,
          connectionId,
        };

        const response = await api.post(`/integrations/${app.slug}/connect`, payload) as {
          success: boolean;
          data: { profileId: string; connectionId: string };
        };

        if (!response.success) {
          throw new Error("Failed to create connection");
        }

        const profileId = response.data.profileId;
        setCreatedProfileId(profileId);
        setCreatedConnectedAccountId(response.data.connectionId);

        // Internal apps use API key auth - no OAuth redirect needed
        // Create a profile object compatible with ComposioProfile
        const profile: ComposioProfile = {
          profileId,
          profileName: profileName.trim(),
          toolkitName: app.name,
          toolkitSlug: app.slug,
          isConnected: true,
          createdAt: new Date().toISOString(),
          mcpUrl: "",
          displayName: profileName.trim(),
          isDefault: false,
          connectedAccountId: response.data.connectionId,
        };

        onProfileConnected(profile);
        // Only navigate to success and close dialog if not embedded
        // Embedded mode lets parent (app-connector) handle navigation
        if (!isEmbedded) {
          navigateToStep(Step.Success);

          // Close dialog after a short delay to show success state
          setTimeout(() => {
            onOpenChange(false);
          }, 1500);
        }
      } else {
        // Handle Composio app connection
        const payload: Record<string, unknown> = {
          name: profileName.trim(),
          appId: app.slug,
        };

        // Set auth mode and params based on toolkit's auth config
        if (hasAuthConfig) {
          const activeConfig = authConfigDetails.find(
            (config: { mode: string }) => config.mode === selectedAuthMethod
          );
          if (activeConfig) {
            payload.authMode = selectedAuthMethod;
            if (selectedAuthMethod === "API_KEY") {
              payload.params = initiationFields;
            } else if (useCustomCredentials && Object.keys(customOAuthFields).length > 0) {
              payload.authConfig = customOAuthFields;
            }
          }
        } else {
          // Standard apps without authConfigDetails
          payload.initiationFields = Object.keys(initiationFields).length > 0 ? initiationFields : undefined;
        }

        const response = (await api.post("/composio/profiles", payload)) as { data: { profile: ComposioProfile } };

        const profile = response.data.profile || {};

        setCreatedProfileId(profile.profileId);
        setCreatedConnectedAccountId(profile.connectedAccountId || null);

        // Determine authentication method based on redirectUrl and selected auth mode
        const hasRedirectUrl = profile.redirectUrl && profile.redirectUrl.trim() !== "";
        const isOAuth = hasAuthConfig
          ? (hasRedirectUrl && selectedAuthMethod === "OAUTH2")
          : hasRedirectUrl;

        if (isOAuth) {
          // OAuth authentication - show redirect dialog and open OAuth popup window
          setRedirectUrl(profile.redirectUrl ?? null);
          navigateToStep(Step.Connecting);

          // Open as centered popup window with fixed dimensions
          const width = 600;
          const height = 700;
          const left = (window.screen.width - width) / 2;
          const top = (window.screen.height - height) / 2;

          window.open(
            profile.redirectUrl,
            "oauth_popup",
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes`
          );
        } else {
          // API_KEY authentication - skip OAuth dialog and complete directly
          await handleAuthComplete();
        }
      }
    } catch (error) {
      console.error("Failed to create profile:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create connection");
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Handles completion of authentication (both OAuth and API key)
   * Finalizes profile connection and calls completion handler
   */
  const handleAuthComplete = async () => {
    if (createdProfileId) {
      try {
        /**
         * Verify the connection is actually active on Composio before accepting
         * Prevents saving invalid connections when user closes OAuth without completing
         */
        if (createdConnectedAccountId) {
          let connectionStatus: string | undefined;
          try {
            const statusResponse = await api.get(
              `/composio/integration/${createdConnectedAccountId}/status`
            );
            connectionStatus = statusResponse?.data?.status || statusResponse?.status;
          } catch {
            // Status check failed — treat as not connected
          }

          if (connectionStatus !== "ACTIVE") {
            toast.error("Connection not completed", {
              description: "Please complete the authorization in the popup window before continuing.",
            });
            return;
          }
        }

        // Fetch profiles to get the latest state
        await fetchProfiles();

        // Look for the created profile in the fetched profiles
        let profile = profiles.find((p) => p.profileId === createdProfileId && p.isConnected);

        // If profile is not found in fetched profiles (can happen with API key due to sync delays),
        // create a profile object using the data we already have
        if (!profile) {
          profile = {
            profileId: createdProfileId,
            profileName: profileName,
            toolkitName: app.name,
            toolkitSlug: app.slug,
            isConnected: true,
            createdAt: new Date().toISOString(),
            mcpUrl: "",
            displayName: profileName,
            isDefault: false,
            connectedAccountId: createdConnectedAccountId || undefined,
          } as ComposioProfile;
        }

        onProfileConnected(profile);
        // Only navigate to success and close dialog if not embedded
        // Embedded mode lets parent (app-connector) handle navigation
        if (!isEmbedded) {
          navigateToStep(Step.Success);

          // Close dialog after a short delay to show success state
          setTimeout(() => {
            onOpenChange(false);
          }, 1500);
        }
      } catch (error) {
        console.error("Error completing authentication:", error);
        toast.error("Failed to complete authentication. Please try again.");
      }
    }
  };

  /**
   * Handles back navigation between workflow steps
   * Manages step transitions and dialog closure
   */
  const handleBack = () => {
    switch (currentStep) {
      case Step.ProfileCreate:
        if (!showBackButton && onBack) {
          onBack();
        } else {
          navigateToStep(Step.ProfileSelect);
        }
        break;
      case Step.Connecting:
        navigateToStep(Step.ProfileCreate);
        break;
      default:
        if (onBack) {
          onBack();
        }
        break;
    }
  };

  /**
   * Handles the connect action for profile creation
   * Creates profile and initiates authentication flow
   */
  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await handleCreateProfile();
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * Cancels the connection process
   * Returns to profile creation step and clears redirect URL
   */
  const cancelConnection = async () => {
    // Clean up the orphaned profile on Composio when user cancels OAuth
    if (createdProfileId) {
      try {
        await api.delete(`/composio/profiles/${createdProfileId}`);
      } catch {
        // Silently ignore cleanup errors
      }
    }
    setRedirectUrl(null);
    setIsConnecting(false);
    setCreatedProfileId(null);
    setCreatedConnectedAccountId(null);
    navigateToStep(Step.ProfileCreate);
  };

  /**
   * Handles using an existing profile
   * Selects existing profile and completes connection
   */
  const handleUseProfile = () => {
    handleProfileSelect();
  };

  /**
   * Animation variants for step transitions
   * Defines enter, center, and exit animations
   */
  const slideVariants = {
    enter: (direction: "forward" | "backward") => ({
      x: direction === "forward" ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: "forward" | "backward") => ({
      x: direction === "forward" ? -300 : 300,
      opacity: 0,
    }),
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full",
        isEmbedded ? "overflow-hidden" : "overflow-hidden gap-0 max-w-3xl",
        !isEmbedded && "p-0"
      )}
    >
      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden min-h-0">
        <AnimatePresence mode="wait" custom={direction}>
          {currentStep === Step.ProfileSelect && (
            <motion.div
              key="profile-select"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col h-full"
            >
              <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent">
                <div className="py-4 px-8 space-y-6">
                  <div className="space-y-4">
                    <div className="grid gap-3">
                      {existingProfiles.length > 0 && (
                        <Card
                          className={cn(
                            "cursor-pointer p-0 transition-all",
                            selectedConnectionType === "existing"
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-border/80"
                          )}
                          onClick={() => {
                            if (selectedConnectionType === "existing") {
                              setSelectedConnectionType(null);
                              setSelectedProfileId("");
                            } else {
                              setSelectedConnectionType("existing");
                              setSelectedProfileId(
                                existingProfiles[0]?.profileId || ""
                              );
                            }
                          }}
                        >
                          <CardContent className="p-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-green-200 dark:bg-green-900/20 flex items-center justify-center">
                                  <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                  <h5 className="font-medium text-sm">
                                    Use Existing Connection
                                  </h5>
                                  <p className="text-xs text-muted-foreground">
                                    {existingProfiles.length} profile
                                    {existingProfiles.length > 1
                                      ? "s"
                                      : ""}{" "}
                                    already connected
                                  </p>
                                </div>
                              </div>
                              <ChevronRight
                                className={cn(
                                  "w-4 h-4 text-muted-foreground transition-transform",
                                  selectedConnectionType === "existing" &&
                                  "rotate-90"
                                )}
                              />
                            </div>
                            <AnimatePresence>
                              {selectedConnectionType === "existing" && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{
                                    duration: 0.2,
                                    ease: "easeInOut",
                                  }}
                                  className="overflow-hidden"
                                >
                                  <div className="mt-3 pt-3 border-t border-border/50">
                                    <Select
                                      value={selectedProfileId}
                                      onValueChange={setSelectedProfileId}
                                    >
                                      <SelectTrigger className="w-full h-10">
                                        <SelectValue placeholder="Select a profile..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {existingProfiles.map((profile) => (
                                          <SelectItem
                                            key={profile.profileId}
                                            value={profile.profileId}
                                          >
                                            <div className="flex items-center gap-3">
                                              {app.logo ? (
                                                <img
                                                  src={app.logo}
                                                  alt={app.name}
                                                  className="w-5 h-5 rounded-lg object-contain bg-muted p-0.5 border flex-shrink-0"
                                                />
                                              ) : (
                                                <div className="w-5 h-5 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center text-primary text-xs font-semibold flex-shrink-0">
                                                  {app.name.charAt(0)}
                                                </div>
                                              )}
                                              <div>
                                                <div className="text-sm font-medium">
                                                  {profile.profileName}
                                                </div>
                                              </div>
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </CardContent>
                        </Card>
                      )}

                      <Card
                        className={cn(
                          "cursor-pointer p-0 transition-all",
                          selectedConnectionType === "new"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-border/80"
                        )}
                        onClick={() => {
                          if (selectedConnectionType === "new") {
                            setSelectedConnectionType(null);
                            setSelectedProfileId("");
                          } else {
                            setSelectedConnectionType("new");
                            setSelectedProfileId("new");
                          }
                        }}
                      >
                        <CardContent className="p-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Plus className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <h5 className="font-medium text-sm">
                                  Create New Connection
                                </h5>
                                <p className="text-xs text-muted-foreground">
                                  Connect a new {app.name} account
                                </p>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                  <AnimatePresence>
                    {showToolsManager && showToolsPreview && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-3">
                          <ScrollArea className="h-[200px] border rounded-md bg-muted/10">
                            <div className="p-2">
                              {isLoadingToolsPreview ? (
                                <div className="grid grid-cols-2 gap-1">
                                  {[...Array(8)].map((_, i) => (
                                    <div
                                      key={i}
                                      className="border rounded-md p-2 animate-pulse bg-background/50"
                                    >
                                      <div className="h-2 bg-muted rounded w-3/4 mb-1"></div>
                                      <div className="h-2 bg-muted rounded w-1/2"></div>
                                    </div>
                                  ))}
                                </div>
                              ) : filteredToolsPreview.length > 0 ? (
                                <div className="grid grid-cols-2 gap-1">
                                  {filteredToolsPreview.map(
                                    (tool: ComposioTool) => (
                                      <ToolPreviewCard
                                        key={tool.slug}
                                        tool={tool}
                                        searchTerm={toolsPreviewSearchTerm}
                                      />
                                    )
                                  )}
                                </div>
                              ) : (
                                <div className="text-center py-6 text-muted-foreground">
                                  <Search className="h-4 w-4 mx-auto mb-2" />
                                  <p className="text-xs">
                                    {toolsPreviewSearchTerm
                                      ? "No matches"
                                      : "No tools available"}
                                  </p>
                                </div>
                              )}
                            </div>
                          </ScrollArea>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-border/50 bg-muted/20 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {selectedConnectionType === "new"
                      ? "Ready to create new connection"
                      : selectedConnectionType === "existing" &&
                        selectedProfileId
                        ? "Profile selected"
                        : selectedConnectionType === "existing"
                          ? "Select a profile to continue"
                          : "Choose how you want to connect"}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      className="px-6"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (selectedConnectionType === "new") {
                          navigateToStep(Step.ProfileCreate);
                        } else {
                          handleProfileSelect();
                        }
                      }}
                      disabled={
                        !selectedConnectionType ||
                        (selectedConnectionType === "existing" &&
                          !selectedProfileId)
                      }
                      className="px-8 min-w-[120px]"
                    >
                      {selectedConnectionType === "new" ? (
                        <>
                          Create Connection
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </>
                      ) : selectedConnectionType === "existing" &&
                        selectedProfileId ? (
                        <>
                          Use Profile
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </>
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === Step.ProfileCreate && (
            <motion.div
              key="profile-create"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col h-full"
            >
              <ScrollArea className="flex-1">
                <div className="px-8 py-6 space-y-6">
                  <div>
                    <Label
                      htmlFor="profileName"
                      className="text-sm font-medium mb-1"
                    >
                      Profile Name
                    </Label>
                    <Input
                      id="profileName"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      placeholder="Enter a name for this profile"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Choose a memorable name to identify this connection
                    </p>
                  </div>

                  {isLoadingToolkitDetails && (
                    <div className="space-y-3">
                      <div className="text-sm text-muted-foreground">
                        Loading connection requirements...
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-3 w-3/4" />
                      </div>
                    </div>
                  )}

                  {!isLoadingToolkitDetails && (
                    <>
                      {/* Auth config-based UI for toolkits with authConfigDetails */}
                      {hasAuthConfig ? (
                        <div className="space-y-4">
                          {/* Auth method selector — only shown when multiple modes available */}
                          {hasMultipleAuthModes && (
                            <div className="space-y-3">
                              <label className="text-sm font-medium">Authentication Method</label>
                              <RadioGroup
                                value={selectedAuthMethod}
                                onValueChange={(value) => setSelectedAuthMethod(value as "API_KEY" | "OAUTH2")}
                              >
                                {availableAuthModes.includes("API_KEY") && (
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="API_KEY" id="auth_api_key" />
                                    <Label htmlFor="auth_api_key" className="text-sm font-normal">
                                      API Key
                                    </Label>
                                  </div>
                                )}
                                {availableAuthModes.includes("OAUTH2") && (
                                  <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="OAUTH2" id="auth_oauth2" />
                                    <Label htmlFor="auth_oauth2" className="text-sm font-normal">
                                      OAuth2
                                    </Label>
                                  </div>
                                )}
                              </RadioGroup>
                            </div>
                          )}

                          {selectedAuthMethod === "API_KEY" ? (
                            <>
                              {/* API Key Authentication Fields */}
                              {authConfigDetails
                                .find((config: { mode: string }) => config.mode === "API_KEY")
                                ?.fields?.connectedAccountInitiation?.required?.map((field: AuthConfigField) => (
                                  <div key={field.name} className="space-y-2">
                                    <Label htmlFor={field.name} className="text-sm font-medium">
                                      {field.displayName || field.name}
                                      {field.required && <span className="text-destructive">*</span>}
                                    </Label>
                                    <Input
                                      id={field.name}
                                      type={field.name.toLowerCase().includes("token") || field.name.toLowerCase().includes("secret") || field.name.toLowerCase().includes("key") ? "password" : "text"}
                                      placeholder={field.description || `Enter ${field.displayName || field.name}`}
                                      value={initiationFields[field.name] || ""}
                                      onChange={(e) => handleInitiationFieldChange(field.name, e.target.value)}
                                      className="w-full"
                                    />
                                    {field.description && (
                                      <p className="text-xs text-muted-foreground">{field.description}</p>
                                    )}
                                    {initiationFieldsErrors[field.name] && (
                                      <p className="text-xs text-destructive">{initiationFieldsErrors[field.name]}</p>
                                    )}
                                  </div>
                                ))}
                            </>
                          ) : (
                            <>
                              {/* OAuth2 Authentication */}
                              <div className="space-y-4">
                                <div className="text-sm text-muted-foreground">
                                  Connect using OAuth2 authentication
                                </div>

                                {/* Custom Credentials Toggle */}
                                <div className="flex items-center justify-between">
                                  <div className="text-sm">Use custom OAuth2 credentials</div>
                                  <Switch
                                    checked={useCustomCredentials}
                                    onCheckedChange={setUseCustomCredentials}
                                  />
                                </div>

                                {useCustomCredentials && (
                                  <div className="space-y-4 border rounded-md p-4 bg-muted/30">
                                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                      Custom OAuth2 Configuration
                                    </div>

                                    {/* Required Fields */}
                                    {(() => {
                                      const oauth2Config = authConfigDetails.find((config: { mode: string }) => config.mode === "OAUTH2");
                                      return oauth2Config?.fields?.authConfigCreation?.required &&
                                        oauth2Config.fields.authConfigCreation.required.length > 0;
                                    })() && (
                                        <div className="space-y-3">
                                          <div className="text-sm font-medium text-red-600">Required Fields</div>
                                          {authConfigDetails
                                            .find((config: { mode: string }) => config.mode === "OAUTH2")
                                            ?.fields?.authConfigCreation?.required?.map((field: AuthConfigField) => (
                                              <div key={field.name} className="space-y-2">
                                                <Label className="text-sm font-medium">
                                                  {field.displayName || field.name}
                                                  {field.required && <span className="text-destructive ml-1">*</span>}
                                                </Label>
                                                <Input
                                                  type={field.name.toLowerCase().includes("secret") || field.name.toLowerCase().includes("token") ? "password" : "text"}
                                                  placeholder={`Enter ${field.displayName || field.name}`}
                                                  value={customOAuthFields[field.name] || ""}
                                                  onChange={(e) => handleCustomOAuthFieldChange(field.name, e.target.value)}
                                                  className="text-sm"
                                                  required={field.required}
                                                />
                                                {field.description && (
                                                  <p className="text-xs text-muted-foreground">{field.description}</p>
                                                )}
                                              </div>
                                            ))}
                                        </div>
                                      )}

                                    {/* Optional Fields */}
                                    {(() => {
                                      const oauth2Config = authConfigDetails.find((config: { mode: string }) => config.mode === "OAUTH2");
                                      return oauth2Config?.fields?.authConfigCreation?.optional &&
                                        oauth2Config.fields.authConfigCreation.optional.length > 0;
                                    })() && (
                                        <div className="space-y-3">
                                          <div className="text-sm font-medium text-muted-foreground">Optional Fields</div>
                                          {authConfigDetails
                                            .find((config: { mode: string }) => config.mode === "OAUTH2")
                                            ?.fields?.authConfigCreation?.optional?.map((field: AuthConfigField) => (
                                              <div key={field.name} className="space-y-2">
                                                <Label className="text-sm">
                                                  {field.displayName || field.name}
                                                </Label>
                                                <Input
                                                  type={field.name.toLowerCase().includes("token") || field.name.toLowerCase().includes("secret") ? "password" : "text"}
                                                  placeholder={field.default || `Enter ${field.displayName || field.name}`}
                                                  value={customOAuthFields[field.name] || field.default || ""}
                                                  onChange={(e) => handleCustomOAuthFieldChange(field.name, e.target.value)}
                                                  className="text-sm"
                                                />
                                                {field.description && (
                                                  <p className="text-xs text-muted-foreground">{field.description}</p>
                                                )}
                                              </div>
                                            ))}
                                        </div>
                                      )}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        /* Standard authentication UI for other apps */
                        <>
                          {toolkitDetails?.toolkit.connectedAccountInitiationFields && (
                            <div className="space-y-4">
                              {(toolkitDetails.toolkit.connectedAccountInitiationFields
                                .required?.length ?? 0) > 0 && (
                                  <div className="text-sm font-medium text-muted-foreground">
                                    Connection Requirements
                                  </div>
                                )}
                              {(toolkitDetails.toolkit.connectedAccountInitiationFields
                                .required?.length ?? 0) > 0 && (
                                  <div className="space-y-3">
                                    {toolkitDetails.toolkit.connectedAccountInitiationFields.required?.map(
                                      (field: AuthConfigField) => (
                                        <InitiationFieldInput
                                          key={field.name}
                                          field={field}
                                          value={initiationFields[field.name] || ""}
                                          onChange={(value) =>
                                            handleInitiationFieldChange(field.name, value)
                                          }
                                          error={initiationFieldsErrors[field.name]}
                                        />
                                      )
                                    )}
                                  </div>
                                )}
                              {(toolkitDetails.toolkit.connectedAccountInitiationFields
                                .optional?.length ?? 0) > 0 && (
                                  <div className="space-y-3">
                                    <div className="text-xs text-muted-foreground">
                                      Optional
                                    </div>
                                    {toolkitDetails.toolkit.connectedAccountInitiationFields.optional?.map(
                                      (field: AuthConfigField) => (
                                        <InitiationFieldInput
                                          key={field.name}
                                          field={field}
                                          value={initiationFields[field.name] || ""}
                                          onChange={(value) =>
                                            handleInitiationFieldChange(field.name, value)
                                          }
                                          error={initiationFieldsErrors[field.name]}
                                        />
                                      )
                                    )}
                                  </div>
                                )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </ScrollArea>
            </motion.div>
          )}

          {currentStep === Step.Connecting && (
            <motion.div
              key="connecting"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col h-full"
            >
              <ScrollArea className="flex-1">
                <div className="px-8 py-6 space-y-6">
                  {!(hasAuthConfig && selectedAuthMethod === "API_KEY") && (
                    <>
                      <div className="text-center space-y-6 py-8">
                        <div className="w-20 h-20 mx-auto border rounded-2xl bg-primary/10 flex items-center justify-center">
                          <ExternalLink className="h-10 w-10 text-primary animate-pulse" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="font-semibold text-lg">
                            Complete Authentication
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            A new window has opened for you to authorize your {app.name}{" "}
                            connection. Complete the process there and return here.
                          </p>
                        </div>
                      </div>
                      {redirectUrl && (
                        <Alert className="bg-muted/50 border-muted">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="text-sm">
                            If the window didn't open,{" "}
                            <button
                              onClick={() => {
                                // Open as centered popup window with fixed dimensions
                                const width = 600;
                                const height = 700;
                                const left = (window.screen.width - width) / 2;
                                const top = (window.screen.height - height) / 2;

                                window.open(
                                  redirectUrl,
                                  "oauth_popup",
                                  `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes`
                                );
                              }}
                              className="underline font-medium hover:no-underline"
                            >
                              click here to authenticate
                            </button>
                          </AlertDescription>
                        </Alert>
                      )}
                    </>
                  )}

                  {hasAuthConfig && selectedAuthMethod === "API_KEY" && (
                    <div className="text-center space-y-6 py-8">
                      <div className="w-20 h-20 mx-auto border rounded-2xl bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                        <Check className="h-10 w-10 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-semibold text-lg">
                          Authentication Complete
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                          Your {app.name} API key has been successfully configured.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </motion.div>
          )}

          {currentStep === Step.Success && (
            <motion.div
              key="success"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="text-center py-8 px-8"
            >
              <div className="space-y-6">
                <div className="w-18 h-18 mx-auto rounded-full bg-green-400 dark:bg-green-600 flex items-center justify-center">
                  <Check className="h-10 w-10 text-white" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-lg">
                    Successfully Connected!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Your {app.name} integration is ready.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Unified Sticky Footer - Show when not embedded OR when embedded but not in ProfileSelect step */}
      {(!isEmbedded || currentStep !== Step.ProfileSelect) && (
        <div className="border-t bg-muted/20 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Tools Preview Toggle - only show when showToolsPreview is enabled */}
              {showToolsPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowToolsManager(!showToolsManager)}
                  className="text-sm"
                >
                  {showToolsManager ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Hide Tools Preview
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Show Tools Preview
                    </>
                  )}
                </Button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Back Button - show for all steps except ProfileSelect when not embedded */}
              {(!isEmbedded || currentStep !== Step.ProfileSelect) &&
                showBackButton && (
                  <Button variant="outline" size="sm" onClick={handleBack}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                )}

              {/* Step-specific action buttons */}
              {currentStep === Step.ProfileCreate && (
                <Button
                  size="sm"
                  onClick={handleConnect}
                  disabled={!profileName.trim() || isConnecting || !canConnect}
                  className="min-w-[100px]"
                >
                  {isConnecting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Connect
                </Button>
              )}

              {currentStep === Step.Connecting && (
                <>
                  <Button variant="outline" size="sm" onClick={cancelConnection}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAuthComplete}
                    disabled={!createdProfileId}
                    className="min-w-[100px]"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Complete
                  </Button>
                </>
              )}

              {currentStep === Step.ProfileSelect && !isEmbedded && (
                <Button
                  size="sm"
                  onClick={handleUseProfile}
                  disabled={!selectedProfileId}
                  className="min-w-[100px]"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Use Profile
                </Button>
              )}

              {currentStep === Step.Success && !isEmbedded && (
                <Button
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  className="min-w-[100px]"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Done
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
